import TextInputLabelHookForm from "components/TextInputLabelHookForm";
import SelectHookFormSingle from "components/SelectHookFormSingle";
import { useVendors } from "../hooks/useVendors";
import { Controller, useFieldArray } from "react-hook-form";
import { useEffect, useState } from "react";
import { CardReaderGroup } from "./CardReaderGroup";
import { CardReaderAdmin } from "./CardReaderAdmin";
import { useSiteAdmin } from "../hooks/useGetSiteAdmin";
import { useGetCardReaderId } from "../hooks/useGetCardReaderById";
import { useRecoilState } from "recoil";
import {
  cardReaderFormState,
  ActionType,
  addCardReaderForm,
  addedCardReader,
  cardReaderEditIndexState,
  updatedCardReader,
} from "../atoms/atoms";
import {
  addSingleCardReader,
  editSingleCardReader,
} from "actions/CarrierAgreementMgmt/Security";
import { toast } from "components/toastNotification";
import CloseIconButton from "components/CloseIconButton";
import { CheckCircleIcon } from "@heroicons/react/24/solid";
import moment from "moment";

export interface CardReaderFormHandles {
  save: () => void;
}

const CardReaderForm = ({
  cardReaderDetails,
  FormMethods,
  isAdd,
  setIsCardReaderOpen,
}) => {
  var { data: cardReaderData } = useGetCardReaderId(cardReaderDetails);
  const [cardReaderActionState, setCardReaderActionState] =
    useRecoilState(cardReaderFormState);
  const [isSubmissionSuccessful, setIsSubmissionSuccessful] = useState(false);
  const [, setNewlyAddedCardReader] = useRecoilState(addedCardReader);
  const [, setNewlyUpdatedCardReader] = useRecoilState(updatedCardReader);

  const {
    trigger,
    register,
    reset,
    control,
    formState,
    watch,
    setValue,
    getValues,
    handleSubmit,
  } = FormMethods;

  const { errors, isDirty } = formState;

  const formatTime = (raw: string | null | undefined): string => {
    if (!raw) return "";

    const m = moment(raw, "HH:mm", true).isValid()
      ? moment(raw, "HH:mm", true)
      : moment(raw, "hh:mma", true);

    return m.isValid() ? m.format("hh:mma") : "";
  };
  const onSubmit = async (data) => {
    const groupInfos = data.groupInfos.map((group) => {
      const formattedRange = (start, end) => {
        if (!start && !end) return "-";

        if (!start || !end) return "-";

        const s = formatTime(start);
        const e = formatTime(end);

        if (!s || !e || s === "Invalid date" || e === "Invalid date")
          return "-";

        return `${s}-${e}`;
      };

      return {
        ...group,
        groupTitle: group.groupTitle?.replace(/\s+/g, " ").trim() || "",

        monday_Hours: formattedRange(
          group.mondayStartTime,
          group.mondayEndTime
        ),
        tuesday_Hours: formattedRange(
          group.tuesdayStartTime,
          group.tuesdayEndTime
        ),
        wednesday_Hours: formattedRange(
          group.wednesdayStartTime,
          group.wednesdayEndTime
        ),
        thursday_Hours: formattedRange(
          group.thursdayStartTime,
          group.thursdayEndTime
        ),
        friday_Hours: formattedRange(
          group.fridayStartTime,
          group.fridayEndTime
        ),
        saturday_Hours: formattedRange(
          group.saturdayStartTime,
          group.saturdayEndTime
        ),
        sunday_Hours: formattedRange(
          group.sundayStartTime,
          group.sundayEndTime
        ),
      };
    });
    data.groupInfos = groupInfos;

    try {
      if (isAdd) {
        return;
      }

      let response;

      if (cardReaderActionState === ActionType.ADD) {
        response = await addSingleCardReader(data);
      } else if (cardReaderActionState === ActionType.EDIT) {
        if (!isDirty) {
          toast.fire("Saving Failed", "No changes have been made.", "warning");
          return;
        }

        response = await editSingleCardReader(data);
      }

      if (response?.status === 200) {
        toast.fire(
          "Success",
          `Card reader ${
            cardReaderActionState === ActionType.ADD ? "added" : "updated"
          } successfully!`,
          "success"
        );

        data.id = response.data.id;
        setSecCardReader([]);
        setIsSubmissionSuccessful(true);

        if (cardReaderActionState === ActionType.ADD) {
          setNewlyAddedCardReader((prev) => [...prev, data]);
        }

        const updatedItem = {
          ...data,
          id: response.data.id ?? data.id,
        };
        if (cardReaderActionState === ActionType.EDIT) {
          const parsedData = JSON.parse(response.config.data);
          const updatedItem = { ...data, id: parsedData.id };

          setNewlyUpdatedCardReader((prev) => {
            const filtered = prev.filter((item) => item.id !== updatedItem.id);
            return [...filtered, updatedItem];
          });
        }
      }
    } catch (error) {
      toast.fire("Error", "Failed to submit card reader data.", "error");
      setIsSubmissionSuccessful(false);
    }
  };

  const {
    fields: groupInfoFields,
    append: appendGroupInfo,
    remove: removeGroupInfo,
  } = useFieldArray({ control, name: "groupInfos" });
  const {
    fields: siteAdminFields,
    append: appendSiteAdmin,
    remove: removeSiteAdmin,
  } = useFieldArray({ control, name: "siteAdmins" });
  const {
    fields: masterSiteAdminFields,
    append: appendMasterSiteAdmin,
    remove: removeMasterSiteAdmin,
  } = useFieldArray({ control, name: "masterAdmin" });

  useEffect(() => {
    return () => {
      setSecCardReader([]);
    };
  }, [isAdd]);

  useEffect(() => {
    if (cardReaderData) {
      let newCardReader = { ...cardReaderData };

      newCardReader.siteAdmins =
        newCardReader?.siteAdmins?.map((item) => ({
          ...item,
          isFieldsDisabled: item.employeeId !== 0,
        })) ?? [];

      newCardReader.masterAdmin =
        newCardReader?.masterAdmin?.map((item) => ({
          ...item,
          isFieldsDisabled: item.employeeId !== 0,
        })) ?? [];

      let masterAdmins = newCardReader.masterAdmin ?? [];

      newCardReader.masterAdmin =
        masterAdmins.length > 0
          ? masterAdmins
          : newCardReader?.siteAdmins?.filter(
              (admin) => admin.isMasterAdmin
            ) ?? [
              {
                employeeId: 0,
                email: "",
                isFieldsDisabled: false,
                isMasterAdmin: true,
                name: "",
                phoneNumber: "",
              },
            ];

      newCardReader.siteAdmins = newCardReader?.siteAdmins?.filter(
        (admin) => !admin.isMasterAdmin
      ) ?? [
        {
          employeeId: 0,
          email: "",
          isFieldsDisabled: false,
          isMasterAdmin: false,
          name: "",
          phoneNumber: "",
        },
      ];

      newCardReader.groupInfos = newCardReader?.groupInfos?.map((groupInfo) => {
        const daysOftheWeekArr =
          groupInfo?.daysOftheWeek === ""
            ? []
            : groupInfo?.daysOftheWeek?.split(",");

        if (!groupInfo.daysOftheWeekArr) {
          groupInfo.daysOftheWeekArr =
            daysOftheWeekArr.map((dotw) => ({
              label: dotw,
              value: dotw,
            })) ?? [];
        }

        return groupInfo;
      }) ?? [
        {
          daysOftheWeek: "",
          daysOftheWeekArr: [],
          doors: [""],
          endTime: "",
          startTime: "",
          groupTitle: "",
          staffGroup: "",

          mondayStartTime: "",
          mondayEndTime: "",

          tuesdayStartTime: "",
          tuesdayEndTime: "",

          wednesdayStartTime: "",
          wednesdayEndTime: "",

          thursdayStartTime: "",
          thursdayEndTime: "",

          fridayStartTime: "",
          fridayEndTime: "",

          saturdayStartTime: "",
          saturdayEndTime: "",

          sundayStartTime: "",
          sundayEndTime: "",
        },
      ];

      newCardReader.groupInfos = newCardReader?.groupInfos ?? [
        {
          daysOftheWeek: "",
          daysOftheWeekArr: [],
          doors: [""],
          endTime: "",
          startTime: "",
          groupTitle: "",
          staffGroup: "",

          mondayStartTime: "",
          mondayEndTime: "",

          tuesdayStartTime: "",
          tuesdayEndTime: "",

          wednesdayStartTime: "",
          wednesdayEndTime: "",

          thursdayStartTime: "",
          thursdayEndTime: "",

          fridayStartTime: "",
          fridayEndTime: "",

          saturdayStartTime: "",
          saturdayEndTime: "",

          sundayStartTime: "",
          sundayEndTime: "",
        },
      ];

      reset(newCardReader);
    }
  }, [cardReaderData, reset]);

  const { data: vendorData } = useVendors();

  const vendorsOption = vendorData?.data?.map(
    (x: {
      id: number;
      name: string;
      contact: string;
      email: string;
      city: string;
      accountNumber: string;
      state: string;
      street: string;
      vendorType: number;
      zipcode: string;
      phone: string;
      tonerRequestEmail: string;
      tonerRequestPhone: string;
    }) => {
      return {
        id: x.id,
        value: x.id,
        label: x.name,
        email: x.email,
        accountNumberBrivo: x.accountNumber,
        address:
          (x.street ? x.street : "") +
          " " +
          (x.city ? x.city : "") +
          " " +
          (x.state ? x.state : "") +
          " " +
          (x.zipcode ? x.zipcode : ""),
        vendorType: x.vendorType,
        vendorPhone: x.phone,
        trEmail: x.tonerRequestEmail,
        tonerPhone: x.tonerRequestPhone,
        contact: x.contact,
      };
    }
  );
  const { data: siteAdmin } = useSiteAdmin();
  type SiteAdminOption = {
    id: number;
    value: number;
    label: string;
    email: string;
    phone: string;
    fullname: string;
  };

  const [siteAdminsOptions, setSiteAdminsOptions] = useState<SiteAdminOption[]>(
    []
  );

  useEffect(() => {
    const data = siteAdmin?.data;
    if (Array.isArray(data)) {
      const options = data.map(
        (x: {
          employeeId: number;
          fullname: string;
          phone: string;
          email: string;
        }) => ({
          id: x.employeeId,
          value: x.employeeId,
          label: x.fullname,
          email: x.email,
          phone: x.phone,
          fullname: x.fullname,
        })
      );
      options.unshift({
        id: 0,
        value: -1,
        label: "Others",
        email: "",
        phone: "",
        fullname: "",
      });

      setSiteAdminsOptions(options);
    } else {
      setSiteAdminsOptions([
        {
          id: 0,
          value: -1,
          label: "Others",
          email: "",
          phone: "",
          fullname: "",
        },
      ]);
    }
  }, [siteAdmin]);

  const ipAddressSettingOption = [
    { label: "DHCP", value: true },
    { label: "Static IP Address", value: false },
  ];

  const validateValues = () => {
    let formValues = { ...getValues() };

    formValues.groupInfos = formValues.groupInfos.map((groupInfo) => {
      let doors = groupInfo.doors.filter((x) => x);
      let daysOfTheWeek =
        groupInfo?.daysOftheWeekArr?.map((x) => x.value).join(",") ?? "";

      groupInfo.doors = doors;
      groupInfo.daysOftheWeek = daysOfTheWeek;
      return groupInfo;
    });
    return formValues;
  };

  const handleClose = () => {
    setIsCardReaderOpen(false);
    setCardReaderActionState(ActionType.NONE);
    reset();
  };

  useEffect(() => {
    if (isSubmissionSuccessful) {
      handleClose();
      setIsSubmissionSuccessful(false);
    }
  }, [isSubmissionSuccessful]);

  const isWarranty = watch("isWarranty");
  const isKeyCard = watch("isKeyCardAccess");

  if (isWarranty === false) {
    setValue("warrantyCoverage", "");
    setValue("additionalDescription", "");
    setValue("coverageStartDate", "");
    setValue("coverageEndDate", "");
  }

  if (isKeyCard === false) {
    setValue("parkingCardFormat", "");
    setValue("parkingSiteNumber", "");
    setValue("cardName", "");
    setValue("cardCompanyName", "");
    setValue("cardAddress", "");
    setValue("cardHours", "");
    setValue("cardEmailAddress", "");
  }

  //Reset the form
  // useEffect(() => {
  //   if (cardReaderActionState === ActionType.ADD) {
  //     reset({ recordNumber: undefined });
  //   }
  //   // if (cardReaderActionState === ActionType.NONE) {
  //   //   reset();
  //   // }
  // }, [cardReaderActionState]);

  const [secCardReader, setSecCardReader] =
    useRecoilState<any>(addCardReaderForm);

  const [indexEdit, setEditIndex] = useRecoilState(cardReaderEditIndexState);

  const handleProceed = async () => {
    try {
      const isValid = await trigger();

      if (!isValid) return;

      const formData = JSON.parse(JSON.stringify(getValues()));
      formData.isActive = true;

      await new Promise<void>((resolve) => {
        setSecCardReader((prev) => {
          let updatedList;

          if (
            cardReaderActionState === ActionType.EDIT &&
            indexEdit !== null &&
            indexEdit >= 0 &&
            indexEdit < prev.length
          ) {
            updatedList = prev.map((item, idx) =>
              idx === indexEdit ? formData : item
            );
          } else {
            updatedList = [...prev, formData];
          }

          resolve();
          return updatedList;
        });
      });
      handleClose();
      setIsCardReaderOpen(false);
      setEditIndex(null);
    } catch (error) {}
  };

  return (
    <div className="space-y-1 p-3 border-l-2 border-primary box-shadow relative">
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="flex justify-between">
          <div className="font-bold text-primary text-lg flex items-center gap-x-1 pl-2">
            {isAdd ? (
              <button
                className="hover:underline focus:outline-none text-gray-500"
                onClick={() => {
                  setIsCardReaderOpen(false);
                  setCardReaderActionState(ActionType.NONE);
                }}
              >
                Add Security
              </button>
            ) : (
              <button
                className="hover:underline focus:outline-none text-gray-500"
                onClick={() => {
                  setIsCardReaderOpen(false);
                  setCardReaderActionState(ActionType.NONE);
                }}
              >
                Edit Security
              </button>
            )}

            <span className="mx-1">›</span>
            <span className="text-primary font-bold">
              {cardReaderActionState === ActionType.ADD
                ? "Add Card Reader"
                : cardReaderActionState === ActionType.EDIT
                ? "Edit Card Reader"
                : "Security"}
            </span>
          </div>

          <div className="flex gap-x-4">
            <CloseIconButton onClick={() => handleClose()} />

            {isAdd ? (
              <button
                onClick={async () => {
                  await handleProceed();
                }}
                type="button"
                className={`hover:scale-90 transition duration-500 flex items-center shadow-md text-xs 
    font-semibold text-white justify-center gap-x-1 rounded-full ml-auto bg-senary px-3 
    py-1 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-senary 
    focus:ring-opacity-70 mr-6`}
              >
                <CheckCircleIcon className="h-4 w-4" />
                <div className="-mt-auto">Proceed</div>
              </button>
            ) : (
              <button
                type="submit"
                className={`hover:scale-90 transition duration-500 flex items-center shadow-md text-xs 
    font-semibold text-white justify-center gap-x-1 rounded-full ml-auto bg-senary px-3 
    h-6 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-senary 
    focus:ring-opacity-70 mr-6`}
              >
                <CheckCircleIcon className="h-4 w-4" />
                <div className="-mt-auto">Save</div>
              </button>
            )}
          </div>
        </div>

        <div className="border-b-2 border-secondary my-1 mx-2"></div>
        <div className="text-xs text-secondary italic font-normal mt-2 ml-2">
          <b> Note:</b> All fields with * are required to fill.
        </div>

        {/* GENERAL SECTION */}
        <section className=" pb-6 pt-4 px-4 bg-white box-shadow rounded-lg mt-4">
          <div className="text-blue-800 font-semibold text-sm pb-2 ">
            General Details
          </div>

          <div className="grid grid-cols-2 space-x-8">
            <div className="space-y-2">
              <Controller
                name={`vendorId`}
                control={control}
                rules={{ required: "Vendor is Required" }}
                render={({ field }) => (
                  <SelectHookFormSingle
                    field={field}
                    onChange={(e) => {
                      field.onChange(e ? e.value : null);
                      setValue(`vendorPhone`, e ? e.vendorPhone : null);
                      setValue(
                        `accountNumberBrivo`,
                        e ? e.accountNumberBrivo : null
                      );
                      setValue(`vendorName`, e ? e.label : null);
                    }}
                    value={field.value}
                    options={vendorsOption}
                    label="Vendor Name"
                    className={
                      formState.errors.vendorId &&
                      "border-2 border-red-500 col-span-2 "
                    }
                    required={true}
                  />
                )}
              />
              <TextInputLabelHookForm
                name={`accountNumber`}
                label="Account Number"
                type="text"
                register={register(`accountNumberBrivo`, {})}
                maxLength={50}
                disabled
              />
              <TextInputLabelHookForm
                name={`accountNumberBilling`}
                label="Billing Account Number"
                type="text"
                register={register(`accountNumberBilling`, {})}
              />
              <TextInputLabelHookForm
                name={`vendorPhone`}
                label="Vendor Phone"
                type="text"
                register={register(`vendorPhone`, {})}
                maxLength={50}
                disabled={true}
              />
              <TextInputLabelHookForm
                name={`vendorCS`}
                label="Vendor CS#/Site#"
                type="text"
                register={register(`vendorCS`, {})}
                maxLength={100}
              />
              <TextInputLabelHookForm
                name={`brivoSiteNumber`}
                label="Brivo Site Number "
                type="text"
                register={register(`brivoSiteNumber`, {})}
                maxLength={100}
              />
              <TextInputLabelHookForm
                name={`confieSiteNumber`}
                label="Confie Site Number "
                type="text"
                register={register(`confieSiteNumber`, {})}
                maxLength={100}
              />
            </div>
            <div className="space-y-2">
              <TextInputLabelHookForm
                name={`dataConnectionEquipment`}
                label="Data Connection Equipment "
                type="text"
                register={register(`dataConnectionEquipment`, {})}
                maxLength={100}
              />
              <TextInputLabelHookForm
                name={`dataConnectionPort`}
                label="Data Connection Port"
                type="text"
                register={register(`dataConnectionPort`, {})}
                maxLength={100}
              />

              <Controller
                name="ipAddressSettingOption"
                control={control}
                render={({ field }) => {
                  let selectedOption: {
                    label: string;
                    value: boolean;
                  } | null = null;
                  if (field.value === true) {
                    selectedOption = ipAddressSettingOption[0];
                  } else if (field.value === false) {
                    selectedOption = ipAddressSettingOption[1];
                  }

                  const internalFind = ipAddressSettingOption?.find(
                    (c) => c.value === selectedOption?.value
                  );

                  return (
                    <SelectHookFormSingle
                      value={field.value}
                      onChange={(selectedOption) => {
                        field.onChange(
                          selectedOption ? selectedOption.value : null
                        );
                      }}
                      options={ipAddressSettingOption}
                      label="IP Address Setting Options"
                      className={
                        formState.errors.ipAddressSettingOption
                          ? "border-2 border-red-500 col-span-2"
                          : ""
                      }
                    />
                  );
                }}
              />

              <TextInputLabelHookForm
                name={`ipAddress`}
                label="IP Address Setting"
                type="text"
                register={register(`ipAddress`, {})}
                maxLength={150}
              />
              <TextInputLabelHookForm
                name={`installDate`}
                label="Install Date"
                type="date"
                register={register(`installDate`, {})}
              />
              <TextInputLabelHookForm
                name={`cancellationDate`}
                label="Cancellation Date"
                type="date"
                register={register(`cancellationDate`, {})}
              />

              {cardReaderActionState === ActionType.ADD || isAdd ? (
                <TextInputLabelHookForm
                  className=" hidden"
                  name="isActive"
                  // label="Active Account"
                  type="checkbox"
                  register={register("isActive")}
                  isChecked
                />
              ) : (
                <TextInputLabelHookForm
                  className="grid mr-auto !w-4"
                  name="isActive"
                  label="Active Account"
                  type="checkbox"
                  register={register("isActive")}
                />
              )}
            </div>
          </div>
        </section>

        {/* WARRANTY SECTION */}

        <section className=" pb-6 pt-4 px-4 bg-white box-shadow rounded-lg mt-4">
          <div className="text-blue-800 font-semibold text-sm pb-2">
            Warranty Details
          </div>
          <div className="grid grid-cols-2 space-x-8">
            <div className="space-y-2">
              <Controller
                name={`isWarranty`}
                control={control}
                render={({ field }) => (
                  <div className=" w-full text-xs h-8 flex justify-center items-center">
                    <div className="grid grid-cols-1 md:grid-cols-3 w-full items-center gap-y-1">
                      <label className="flex space-x-1 justify-start expandedLabel fontExpand">
                        Warranty
                      </label>
                      <div className="flex gap-4">
                        <label className="flex items-center gap-1 expandedLabel fontExpand">
                          <input
                            type="radio"
                            value="true"
                            checked={field.value === true}
                            onChange={() => {
                              field.onChange(true);
                            }}
                          />
                          Yes
                        </label>
                        <label className="flex items-center gap-1 expandedLabel fontExpand">
                          <input
                            type="radio"
                            value="false"
                            checked={field.value === false}
                            onChange={() => {
                              field.onChange(false);
                            }}
                          />
                          No
                        </label>
                      </div>
                    </div>
                  </div>
                )}
              />

              <TextInputLabelHookForm
                className={
                  errors.warrantyCoverage
                    ? "border border-red-500 focus:ring-red-500 col-span-2"
                    : "border border-slate-300 col-span-2"
                }
                name="warrantyCoverage"
                label="Warranty Coverage"
                type="text"
                // register={register("warrantyCoverage" as const)}
                register={register("warrantyCoverage" as const, {
                  required: watch("isWarranty")
                    ? "Warranty Coverage is required"
                    : false,
                })}
                maxLength={100}
                disabled={!watch(`isWarranty`)}
                required={watch("isWarranty")}
              />

              <div>
                <TextInputLabelHookForm
                  name="additionalDescription"
                  label="Additional Description"
                  type="text"
                  register={register("additionalDescription" as const)}
                  // register={register("additionalDescription" as const, {
                  //   required: watch("isWarranty")
                  //     ? "Additional Description is required"
                  //     : false,
                  // })}
                  maxLength={100}
                  disabled={!watch("isWarranty")}
                  // required={watch("isWarranty")}
                />

                {errors.additionalDescription && (
                  <p className="text-red-500 text-xs mt-1">
                    {errors.additionalDescription.message}
                  </p>
                )}
              </div>
            </div>
            <div className="space-y-2">
              <TextInputLabelHookForm
                name={`coverageStartDate`}
                label="Coverage Start Date"
                type="date"
                register={register(`coverageStartDate`, {})}
                disabled={!watch(`isWarranty`)}
              />
              <TextInputLabelHookForm
                name={`coverageEndDate`}
                label="Coverage End Date"
                type="date"
                register={register(`coverageEndDate`, {})}
                disabled={!watch(`isWarranty`)}
              />
            </div>
          </div>
        </section>

        {/* OTHER CARD DETAILS SECTION */}

        <section className=" pb-6 pt-4 px-4 bg-white box-shadow rounded-lg mt-4">
          <div className="text-blue-800 font-semibold text-sm pb-2">
            Other Card Details
          </div>
          <div className="grid grid-cols-2 space-x-8">
            <div className="space-y-2">
              <TextInputLabelHookForm
                name={`cardType`}
                label="Card Type"
                type="text"
                register={register(`cardType`, {})}
                maxLength={50}
              />

              <Controller
                name={`isKeyCardAccess`}
                control={control}
                rules={{ required: false }}
                render={({ field }) => (
                  <div className=" w-full text-xs h-6 flex justify-center items-center">
                    <div className="grid grid-cols-1 md:grid-cols-3 w-full items-center gap-y-1">
                      <label className="flex space-x-1 justify-start expandedLabel fontExpand">
                        Parking Garage Key Card Access
                      </label>
                      <div className="flex gap-4">
                        <label className="flex items-center gap-1 expandedLabel fontExpand">
                          <input
                            type="radio"
                            value="true"
                            checked={field.value === true}
                            onChange={() => {
                              field.onChange(true);
                            }}
                          />
                          Yes
                        </label>
                        <label className="flex items-center gap-1 expandedLabel fontExpand">
                          <input
                            type="radio"
                            value="false"
                            checked={field.value === false}
                            onChange={() => {
                              field.onChange(false);

                              // formMethods.setValue(`keyCardAccess`, []);
                            }}
                          />
                          No
                        </label>
                      </div>
                    </div>
                    {/* Optional error message */}
                    {formState.errors[`isKeyCardAccess`] && (
                      <span className="text-red-500 text-sm mt-1">
                        Key card access selection is required.
                      </span>
                    )}
                  </div>
                )}
              />

              <TextInputLabelHookForm
                name={`parkingCardFormat`}
                label="Parking Card Format"
                type="text"
                register={register(`parkingCardFormat`, {})}
                maxLength={50}
                disabled={!watch(`isKeyCardAccess`)}
              />
              <TextInputLabelHookForm
                name={`parkingSiteNumber`}
                label="Parking Site Number"
                type="text"
                register={register(`parkingSiteNumber`, {})}
                maxLength={50}
                disabled={!watch(`isKeyCardAccess`)}
              />
            </div>
            <div className="space-y-2">
              <TextInputLabelHookForm
                name={`cardName`}
                label="Card Name"
                type="text"
                register={register(`cardName`, {})}
                maxLength={50}
                disabled={!watch(`isKeyCardAccess`)}
              />
              <TextInputLabelHookForm
                name={`cardCompanyName`}
                label="Company Name"
                type="text"
                register={register(`cardCompanyName`, {})}
                maxLength={50}
                disabled={!watch(`isKeyCardAccess`)}
              />
              <TextInputLabelHookForm
                name={`cardAddress`}
                label="Address"
                type="text"
                register={register(`cardAddress`, {})}
                maxLength={50}
                disabled={!watch(`isKeyCardAccess`)}
              />
              <TextInputLabelHookForm
                name={`cardHours`}
                label="Hours"
                type="time"
                register={register(`cardHours`, {})}
                maxLength={50}
                disabled={!watch(`isKeyCardAccess`)}
              />
              <TextInputLabelHookForm
                className={
                  formState.errors.cardEmailAddress
                    ? "border border-red-500 focus:ring-red-500 col-span-2"
                    : "border border-slate-300 col-span-2"
                }
                name={`cardEmailAddress`}
                label="Email Address"
                register={register("cardEmailAddress", {
                  pattern: {
                    value: /^[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}$/i,
                    message: "Invalid email address",
                  },
                })}
                maxLength={50}
                disabled={!watch(`isKeyCardAccess`)}
              />
            </div>
          </div>
        </section>

        <section className="space-y-2 pb-3">
          <CardReaderAdmin
            siteAdminFields={siteAdminFields}
            appendSiteAdmin={appendSiteAdmin}
            removeSiteAdmin={removeSiteAdmin}
            isMasterAdmin={false}
            siteAdminOptions={siteAdminsOptions}
            FormMethods={FormMethods}
          />
          <CardReaderAdmin
            siteAdminFields={masterSiteAdminFields}
            appendSiteAdmin={appendMasterSiteAdmin}
            removeSiteAdmin={removeMasterSiteAdmin}
            isMasterAdmin={true}
            siteAdminOptions={siteAdminsOptions}
            FormMethods={FormMethods}
          />
        </section>

        <div className="bg-white py-4 px-4 box-shadow rounded-lg">
          <section className=" space-y-2">
            <CardReaderGroup
              groupInfoFields={groupInfoFields}
              appendGroupInfo={appendGroupInfo}
              removeGroupInfo={removeGroupInfo}
              FormMethods={FormMethods}
            />
          </section>
        </div>
      </form>
    </div>
  );
};

export default CardReaderForm;
